import { Close } from '@app/components/icons/close-icon';
import { useFlag } from '@app/feature-flags/use-flag';
import discoveryBannerBg from '@app/routes/content/assets/discovery_banner_bg.jpg';
import { FLAG_POST_EVENT_SHOW_SALESFORCE_BANNER } from '@app/utilities/flags';
import { getDashboardBaseUrl } from '@hopin-team/dashboard-constants';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useDownloadUrl } from './use-download-url';
import { useSalesforceInstalled } from './use-salesforce-installed';

const SALESFORCE_LOGO_URL =
  'https://assets-versions-cdn.static.staging.events.ringcentral.com/assets/v2/integrations/salesforce1-cdf9dbc99627a337e4fa122073983518bbc07edec1d55f57bf2449649a11e9f9.png';

const RECONNECTION_GUIDE_FILENAME = 'salesforce-reconnection-guidelines.pdf';

export const SalesforceReconnectBanner = () => {
  const { t } = useTranslation();
  const showBanner = useFlag(FLAG_POST_EVENT_SHOW_SALESFORCE_BANNER);
  const [visible, setVisible] = useState(true);
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const bannerEnabled =
    Boolean(showBanner) && visible && Boolean(organizationSlug);
  const { isSalesforceInstalled, isLoading } = useSalesforceInstalled({
    enabled: bannerEnabled,
  });
  const { url: reconnectionGuidePdfUrl } = useDownloadUrl(
    RECONNECTION_GUIDE_FILENAME,
    { enabled: bannerEnabled && isSalesforceInstalled },
  );

  if (
    !showBanner ||
    !visible ||
    !organizationSlug ||
    isLoading ||
    !isSalesforceInstalled
  )
    return null;

  const reconnectUrl = `${getDashboardBaseUrl()}/org/${organizationSlug}/apps?app_installed=salesforce`;

  return (
    <div className="relative w-full mb-6">
      <div
        className="w-full flex items-center justify-between rounded-lg bg-cover bg-center overflow-hidden shadow-[inset_0_0_0_1px_rgba(0,0,0,0.2)]"
        style={{
          backgroundImage: `url(${discoveryBannerBg})`,
        }}
      >
        <div className="flex flex-col py-5 pl-8 pr-6 max-w-[65%] gap-y-2">
          <h3 className="font-inter text-neutral-b0 text-[20px] font-medium leading-tight mb-1">
            {t('salesforce-reconnect-banner.title')}
          </h3>
          <p className="font-inter text-neutral-b0 text-sm mb-2">
            {t('salesforce-reconnect-banner.description')}{' '}
            {reconnectionGuidePdfUrl && (
              <a
                href={reconnectionGuidePdfUrl}
                target="_blank"
                rel="noreferrer"
                className="text-neutral-b0 underline cursor-pointer hover:opacity-80"
              >
                {t('salesforce-reconnect-banner.download-guide')}
              </a>
            )}
          </p>
          <a
            href={reconnectUrl}
            className="w-fit flex items-center justify-center px-4 py-2 rounded-full font-semibold bg-white no-underline cursor-pointer hover:bg-white/90"
          >
            <p className="mb-0 text-neutral-b0 text-sm">
              {t('salesforce-reconnect-banner.cta')}
            </p>
          </a>
        </div>
        <div className="flex items-center justify-center pr-8 shrink-0">
          <div className="h-20 w-24 rounded-[4px] overflow-hidden shrink-0">
            <img
              src={SALESFORCE_LOGO_URL}
              alt="Salesforce"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
      <button
        aria-label={t('salesforce-reconnect-banner.dismiss')}
        onClick={() => setVisible(false)}
        className="absolute top-2 right-2 flex items-center justify-center size-5 rounded-full bg-white/60 hover:bg-white/90 text-neutral-b0 transition-colors cursor-pointer border-none"
      >
        <Close className="size-2" />
      </button>
    </div>
  );
};
